import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIAFinanciera } from "@/hooks/useIAFinanciera";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Brain, AlertTriangle, CheckCircle2, CalendarClock } from "lucide-react";
import { WeeklyReportModal } from "./WeeklyReportModal";

function MetricCard({ label, value, color }) {
  const colors = {
    gray:  "bg-white border-gray-200 text-gray-800",
    red:   "bg-red-50 border-red-200 text-red-700",
    green: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`flex-1 min-w-[120px] border rounded-lg p-3 ${colors[color] || colors.gray}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <p className="text-base font-bold leading-tight">{value}</p>
    </div>
  );
}

export const IAFinanceiraTab = () => {
  const { t } = useTranslation();
  const { data, loading, month, setMonth, formatPrice } = useIAFinanciera();
  const [showWeekly, setShowWeekly] = useState(false);

  const statusLabel = {
    critical: t("iaFin.critical"),
    warning:  t("iaFin.warning"),
    healthy:  t("iaFin.ok"),
  };

  const priorityLabel = (p) => {
    if (p === "urgente") return t("iaFin.priorityUrgent");
    if (p === "alta")    return t("iaFin.priorityHigh");
    return t("iaFin.priorityMedium");
  };

  const actualByCategory = useMemo(() => {
    if (!data) return {};
    const map = {};
    [...(data.critical_points || []), ...(data.warnings || [])].forEach(p => {
      map[p.category] = { pct: p.actual_pct, status: p.status };
    });
    (data.healthy || []).forEach(h => {
      map[h.category] = { pct: h.actual_pct, status: "healthy" };
    });
    return map;
  }, [data]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return null;

  const scoreColor = data.health_score >= 70 ? "#047857" : data.health_score >= 40 ? "#d97706" : "#dc2626";
  const scoreCardClass = data.health_score >= 70
    ? "bg-emerald-50 border-emerald-200"
    : data.health_score >= 40
    ? "bg-amber-50 border-amber-200"
    : "bg-red-50 border-red-200";

  return (
    <div className="space-y-6" data-testid="ia-financeira-tab">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#CDA34F]" />
          <h3 className="font-bold text-lg">{t("iaFin.title")}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => setShowWeekly(true)}
            data-testid="weekly-report-btn"
          >
            <CalendarClock className="w-3.5 h-3.5" />
            {t("iaFin.weeklyReport")}
          </Button>
          <Input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            className="w-44"
            data-testid="ia-fin-month"
          />
        </div>
      </div>

      {/* Health Score */}
      <Card className={scoreCardClass}>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start gap-4">
            {/* Score */}
            <div className="min-w-[120px]">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">{t("iaFin.healthScore")}</p>
              <p className="text-6xl font-black leading-none" style={{ color: scoreColor }}>{data.health_score}%</p>
            </div>
            {/* 3 metric cards */}
            <div className="flex flex-wrap gap-3 flex-1">
              <MetricCard
                label={t("iaFin.revenue")}
                value={formatPrice(data.revenue)}
                color="gray"
              />
              <MetricCard
                label={t("iaFin.costs")}
                value={`-${formatPrice(data.total_costs)}`}
                color="red"
              />
              <MetricCard
                label={t("iaFin.result")}
                value={`${formatPrice(data.result_liquid)} (${data.margin_pct}%)`}
                color={data.result_liquid >= 0 ? "green" : "red"}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Critical Points */}
      {data.critical_points.length > 0 && (
        <div className="space-y-3" data-testid="critical-points">
          <h4 className="text-base font-bold text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />{t("iaFin.criticalPoints")}
          </h4>
          {data.critical_points.map((cp, i) => (
            <Card key={i} className="border-red-200 bg-red-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">{statusLabel[cp.status] || cp.status}</span>
                      <span className="text-xs text-gray-500">{cp.label}</span>
                    </div>
                    <p className="text-sm text-gray-800">{cp.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{t("iaFin.value")}: {formatPrice(cp.actual_value)}</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-400">{t("iaFin.ideal")}: {cp.ideal_min}-{cp.ideal_max}%</span>
                        <span className="font-bold text-red-600">{t("iaFin.current")}: {cp.actual_pct}%</span>
                      </div>
                      <div className="h-1.5 bg-red-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500 rounded-full transition-all"
                          style={{ width: `${Math.min(cp.actual_pct / Math.max(cp.ideal_max * 1.5, cp.actual_pct) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Warnings */}
      {data.warnings.length > 0 && (
        <div className="space-y-3" data-testid="warnings">
          <h4 className="text-base font-bold text-amber-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />{t("iaFin.warnings")}
          </h4>
          {data.warnings.map((w, i) => (
            <Card key={i} className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{t("iaFin.warning")}</span>
                      <span className="text-xs text-gray-500">{w.label}</span>
                    </div>
                    <p className="text-sm text-gray-800">{w.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{t("iaFin.value")}: {formatPrice(w.actual_value)}</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-400">{t("iaFin.ideal")}: {w.ideal_min}-{w.ideal_max}%</span>
                        <span className="font-bold text-amber-600">{t("iaFin.current")}: {w.actual_pct}%</span>
                      </div>
                      <div className="h-1.5 bg-amber-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all"
                          style={{ width: `${Math.min(w.actual_pct / Math.max(w.ideal_max * 1.5, w.actual_pct) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Healthy indicators */}
      {data.healthy.length > 0 && (
        <div className="space-y-2" data-testid="healthy-items">
          <h4 className="text-base font-bold text-emerald-600 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />{t("iaFin.healthyIndicators")}
          </h4>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.healthy.map((h, i) => (
              <div key={i} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-sm text-gray-700">{h.label}: <b className="text-emerald-600">{h.actual_pct}%</b></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Recommendations */}
      {data.recommendations.length > 0 && (
        <Card className="border-[#CDA34F]/30 bg-[#CDA34F]/5" data-testid="ai-recommendations">
          <CardContent className="p-5 space-y-4">
            <h4 className="text-base font-bold flex items-center gap-2 text-[#CDA34F]">
              <Brain className="w-4 h-4" />{t("iaFin.aiRecommendations")}
            </h4>
            <div className="space-y-2">
              {data.recommendations.map((rec, i) => (
                <div key={i} className={`rounded-lg border p-3 flex items-start gap-3 ${
                  rec.priority === "urgente" ? "bg-red-50 border-red-200"
                  : rec.priority === "alta"  ? "bg-orange-50 border-orange-200"
                  : "bg-amber-50 border-amber-200"
                }`}>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 mt-0.5 ${
                    rec.priority === "urgente" ? "bg-red-100 text-red-700"
                    : rec.priority === "alta"  ? "bg-orange-100 text-orange-700"
                    : "bg-amber-100 text-amber-700"
                  }`}>
                    {priorityLabel(rec.priority)}
                  </span>
                  <p className="text-sm text-gray-700 leading-relaxed">{rec.text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benchmarks Reference */}
      <Card>
        <CardContent className="p-5">
          <h4 className="text-base font-bold text-gray-600 mb-3">{t("iaFin.marketBenchmarks")}</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(data.benchmarks || {}).filter(([k]) => k !== "result_liquid").map(([key, b]) => {
              const actual = actualByCategory[key];
              return (
                <div key={key} className={`flex items-center justify-between rounded-lg px-3 py-2 border ${
                  actual?.status === "critical" ? "bg-red-50 border-red-200"
                  : actual?.status === "warning" ? "bg-amber-50 border-amber-200"
                  : actual?.status === "healthy" ? "bg-emerald-50 border-emerald-200"
                  : "bg-gray-50 border-gray-200"
                }`}>
                  <span className="text-xs text-gray-600">{b.label}</span>
                  <div className="text-right">
                    {actual && (
                      <span className={`text-xs font-bold mr-2 ${
                        actual.status === "critical" ? "text-red-600"
                        : actual.status === "warning" ? "text-amber-600"
                        : "text-emerald-600"
                      }`}>{actual.pct}%</span>
                    )}
                    <span className="text-xs text-gray-400">({b.ideal_min}-{b.ideal_max}%)</span>
                  </div>
                </div>
              );
            })}
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 border ${
              actualByCategory["result_liquid"]?.status === "critical" ? "bg-red-50 border-red-200"
              : actualByCategory["result_liquid"]?.status === "warning" ? "bg-amber-50 border-amber-200"
              : "bg-emerald-50 border-emerald-200"
            }`}>
              <span className="text-xs text-emerald-700">{t("iaFin.idealProfitMargin")}</span>
              <div className="text-right">
                {actualByCategory["result_liquid"] && (
                  <span className={`text-xs font-bold mr-2 ${
                    actualByCategory["result_liquid"].status === "critical" ? "text-red-600"
                    : actualByCategory["result_liquid"].status === "warning" ? "text-amber-600"
                    : "text-emerald-600"
                  }`}>{actualByCategory["result_liquid"].pct}%</span>
                )}
                <span className="text-xs text-gray-400">(10-20%)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* No data state */}
      {data.orders_count === 0 && (
        <Card className="border-border/50">
          <CardContent className="py-12 text-center">
            <Brain className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground font-medium">{t("iaFin.noData")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("iaFin.noDataDesc")}</p>
          </CardContent>
        </Card>
      )}

      {/* Weekly Report Modal */}
      <WeeklyReportModal open={showWeekly} onClose={() => setShowWeekly(false)} />
    </div>
  );
};
